import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

export const TransactionSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  source: z.string(),
  sourceTransactionId: z.string(),
  sourceAccountId: z.string(),
  accountName: z.string().nullable().optional(),
  amount: DecimalStringSchema,
  direction: z.string(),
  currency: z.string(),
  transactionDate: z.string(),
  authorizedAt: z.string().nullable().optional(),
  postedAt: z.string().nullable().optional(),
  merchantName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  pending: z.boolean(),
  referenceNumber: z.string().nullable().optional(),
  sourceJson: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

export const ListTransactionsInputSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  accountIds: z.array(z.string()).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
  source: z.string().optional(),
});

export const ListTransactionsResponseSchema = z.object({
  transactions: z.array(TransactionSchema),
  total: z.int().nonnegative(),
  lastSyncTime: z.string().nullable().optional(),
});

export const SyncTransactionsInputSchema = z.object({
  accountIds: z.array(z.string()).optional(),
  dateFrom: z.string(),
  dateTo: z.string(),
});

export const SyncTransactionsResponseSchema = z.object({
  synced: z.int().nonnegative(),
  mocked: z.int().nonnegative(),
  autoVerifiedAccounts: z
    .array(
      z.object({
        accountId: z.string(),
        accountName: z.string(),
      }),
    )
    .optional(),
});

export const BalanceSchema = z.object({
  accountId: z.string(),
  name: z.string(),
  officialName: z.string().nullable().optional(),
  mask: z.string().nullable().optional(),
  current: DecimalStringSchema,
  available: DecimalStringSchema.nullable().optional(),
  isoCurrencyCode: z.string(),
});

export const ListBalancesResponseSchema = z.object({
  balances: z.array(BalanceSchema),
});

export const transactionsContracts = {
  list: oc
    .input(ListTransactionsInputSchema)
    .output(ListTransactionsResponseSchema)
    .route({ method: "GET" }),
  sync: oc
    .input(SyncTransactionsInputSchema)
    .output(SyncTransactionsResponseSchema)
    .route({ method: "POST" }),
  listBalances: oc.output(ListBalancesResponseSchema).route({ method: "GET" }),
} as const;

export type Transaction = z.infer<typeof TransactionSchema>;
export type ListTransactionsInput = z.infer<typeof ListTransactionsInputSchema>;
export type ListTransactionsResponse = z.infer<
  typeof ListTransactionsResponseSchema
>;
export type SyncTransactionsInput = z.infer<typeof SyncTransactionsInputSchema>;
export type SyncTransactionsResponse = z.infer<
  typeof SyncTransactionsResponseSchema
>;
export type Balance = z.infer<typeof BalanceSchema>;
export type ListBalancesResponse = z.infer<typeof ListBalancesResponseSchema>;
